import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Upload, Search, Calendar, FileText, Link as LinkIcon, Loader2, Trash2, Edit } from "lucide-react";
import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { useToast } from "@/hooks/use-toast";
import { CreateMeetingDialog } from "@/components/crm/CreateMeetingDialog";

interface Meeting {
  id: string;
  title: string;
  start_at: string;
  end_at: string | null;
  transcript_url: string | null;
  provider: string | null;
}

interface Note {
  id: string;
  title: string | null;
  body: string | null;
  source: string | null;
  created_at: string;
  contact_id: string | null;
  family_id: string | null;
  contacts?: { full_name: string } | null;
  families?: { name: string } | null;
}

interface Contact {
  id: string;
  full_name: string;
}

export default function MeetingsAndNotes() {
  const queryClient = useQueryClient();
  const { orgId, familyId, isMFO } = useTenant();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [noteForm, setNoteForm] = useState({
    title: "",
    body: "",
    source: "manual" as string,
    contact_id: "" as string,
  });

  // Fetch client_id for the current org (required for inserts)
  const { data: clientData } = useQuery({
    queryKey: ["client-for-org", orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("clients")
        .select("id")
        .eq("org_id", orgId!)
        .limit(1)
        .single();
      if (error) return null;
      return data;
    },
    enabled: !!orgId,
  });
  const clientId = clientData?.id || null;

  // Fetch meetings
  const { data: meetings = [], isLoading: meetingsLoading } = useQuery({
    queryKey: ["meetings", orgId],
    queryFn: async () => {
      if (!orgId) return [];
      const { data, error } = await supabase
        .from("meetings")
        .select("*")
        .eq("org_id", orgId)
        .order("start_at", { ascending: false });
      if (error) throw error;
      return (data || []) as Meeting[];
    },
    enabled: !!orgId,
  });

  // Fetch notes from database
  const { data: notes = [], isLoading: notesLoading } = useQuery({
    queryKey: ["notes", orgId, familyId],
    queryFn: async () => {
      if (!orgId) return [];
      let query = supabase
        .from("notes")
        .select(`
          *,
          contacts:contact_id (full_name),
          families:family_id (name)
        `)
        .eq("org_id", orgId);

      // For SFO, filter by the current family
      if (!isMFO && familyId) {
        query = query.eq("family_id", familyId);
      }

      const { data, error } = await query.order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []) as Note[];
    },
    enabled: !!orgId,
  });

  // Fetch contacts for note linking
  const { data: contacts = [] } = useQuery({
    queryKey: ["contacts-for-notes", orgId],
    queryFn: async () => {
      if (!orgId) return [];
      const { data, error } = await supabase
        .from("contacts")
        .select("id, full_name")
        .eq("org_id", orgId)
        .order("full_name");
      if (error) throw error;
      return (data || []) as Contact[];
    },
    enabled: !!orgId,
  });

  // Create note mutation
  const createNoteMutation = useMutation({
    mutationFn: async (note: typeof noteForm) => {
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await supabase.from("notes").insert({
        title: note.title || null,
        body: note.body || null,
        source: note.source || "manual",
        contact_id: note.contact_id && note.contact_id !== "__none__" ? note.contact_id : null,
        org_id: orgId,
        family_id: familyId || null,
        client_id: clientId,
        created_by: user?.id,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notes"] });
      toast({ title: "Note created successfully" });
      setNoteDialogOpen(false);
      resetNoteForm();
    },
    onError: (error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  // Update note mutation
  const updateNoteMutation = useMutation({
    mutationFn: async ({ id, note }: { id: string; note: typeof noteForm }) => {
      const { error } = await supabase
        .from("notes")
        .update({
          title: note.title || null,
          body: note.body || null,
          source: note.source || "manual",
          contact_id: note.contact_id && note.contact_id !== "__none__" ? note.contact_id : null,
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notes"] });
      toast({ title: "Note updated successfully" });
      setNoteDialogOpen(false);
      setEditingNote(null);
      resetNoteForm();
    },
    onError: (error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  // Delete note mutation
  const deleteNoteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("notes").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notes"] });
      toast({ title: "Note deleted" });
    },
    onError: (error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const resetNoteForm = () => {
    setNoteForm({ title: "", body: "", source: "manual", contact_id: "" });
  };

  const openNewNoteDialog = () => {
    setEditingNote(null);
    resetNoteForm();
    setNoteDialogOpen(true);
  };

  const openEditNoteDialog = (note: Note) => {
    setEditingNote(note);
    setNoteForm({
      title: note.title || "",
      body: note.body || "",
      source: note.source || "manual",
      contact_id: note.contact_id || "",
    });
    setNoteDialogOpen(true);
  };

  const handleSaveNote = () => {
    if (editingNote) {
      updateNoteMutation.mutate({ id: editingNote.id, note: noteForm });
    } else {
      createNoteMutation.mutate(noteForm);
    }
  };

  const filteredMeetings = useMemo(() => {
    return meetings.filter((meeting) =>
      meeting.title.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [meetings, searchQuery]);

  const filteredNotes = useMemo(() => {
    return notes.filter((note) =>
      (note.title?.toLowerCase().includes(searchQuery.toLowerCase()) || false) ||
      (note.body?.toLowerCase().includes(searchQuery.toLowerCase()) || false)
    );
  }, [notes, searchQuery]);

  const meetingsWithTranscripts = meetings.filter((m) => m.transcript_url);
  const transcriptCoverage = meetings.length > 0
    ? ((meetingsWithTranscripts.length / meetings.length) * 100).toFixed(0)
    : "0";

  // Calculate notes this week
  const notesThisWeek = useMemo(() => {
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return notes.filter((n) => new Date(n.created_at) >= weekAgo).length;
  }, [notes]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getSourceBadge = (source: string | null) => {
    switch (source) {
      case "meeting":
        return <Badge variant="secondary">Meeting</Badge>;
      case "email":
        return <Badge variant="outline">Email</Badge>;
      default:
        return <Badge variant="outline">Manual</Badge>;
    }
  };

  return (
    <div className="flex-1 space-y-4 px-6 py-6">

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Meetings & Notes</h1>
          <p className="text-muted-foreground">
            Track meetings, transcripts, and rich text notes
          </p>
        </div>
      </div>

      <Tabs defaultValue="meetings" className="space-y-4">
        <TabsList>
          <TabsTrigger value="meetings">Meetings</TabsTrigger>
          <TabsTrigger value="notes">Notes</TabsTrigger>
        </TabsList>

        {/* Meetings Tab */}
        <TabsContent value="meetings" className="space-y-4">
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm">
              <Upload className="h-4 w-4" />
              Upload Transcript
            </Button>
            <Button size="sm" onClick={() => setCreateDialogOpen(true)}>
              <Plus className="h-4 w-4" />
              Add Meeting
            </Button>
          </div>

          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Total Meetings</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{meetings.length}</div>
                <p className="text-xs text-muted-foreground">Total recorded</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">With Transcripts</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{meetingsWithTranscripts.length}</div>
                <p className="text-xs text-muted-foreground">{transcriptCoverage}% coverage</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Action Items</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">—</div>
                <p className="text-xs text-muted-foreground">From meetings</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Upcoming</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">0</div>
                <p className="text-xs text-muted-foreground">Next 7 days</p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Meeting History</CardTitle>
                  <CardDescription>
                    Upload transcripts from Otter/Fireflies or add manually
                  </CardDescription>
                </div>
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search meetings..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-8 w-[250px]"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {meetingsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : filteredMeetings.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  {searchQuery
                    ? "No meetings found matching your search"
                    : "No meetings yet. Add your first meeting to get started."}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Meeting</TableHead>
                      <TableHead>Start Date & Time</TableHead>
                      <TableHead>End Date & Time</TableHead>
                      <TableHead>Transcript</TableHead>
                      <TableHead>Provider</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredMeetings.map((meeting) => (
                      <TableRow key={meeting.id}>
                        <TableCell className="font-medium">{meeting.title}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            <span>{new Date(meeting.start_at).toLocaleString()}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {meeting.end_at ? new Date(meeting.end_at).toLocaleString() : "—"}
                        </TableCell>
                        <TableCell>
                          {meeting.transcript_url ? (
                            <Badge variant="secondary" className="gap-1">
                              <FileText className="h-3 w-3" />
                              Available
                            </Badge>
                          ) : (
                            <Badge variant="outline">None</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {meeting.provider ? (
                            <Badge variant="outline">{meeting.provider}</Badge>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm">
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notes Tab */}
        <TabsContent value="notes" className="space-y-4">
          <div className="flex justify-end">
            <Button size="sm" onClick={openNewNoteDialog}>
              <Plus className="h-4 w-4" />
              New Note
            </Button>
          </div>

          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Total Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{notes.length}</div>
                <p className="text-xs text-muted-foreground">Across all records</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">With Links</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {notes.filter((n) => n.contact_id).length}
                </div>
                <p className="text-xs text-muted-foreground">Connected to contacts</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">This Week</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{notesThisWeek}</div>
                <p className="text-xs text-muted-foreground">New notes added</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">From Meetings</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {notes.filter((n) => n.source === "meeting").length}
                </div>
                <p className="text-xs text-muted-foreground">Meeting notes</p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Notes & Memos</CardTitle>
                  <CardDescription>
                    Rich text notes linked to contacts and meetings
                  </CardDescription>
                </div>
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search notes..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-8 w-[250px]"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {notesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : filteredNotes.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  {searchQuery
                    ? "No notes found matching your search"
                    : "No notes yet. Create your first note to get started."}
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredNotes.map((note) => (
                    <Card
                      key={note.id}
                      className="hover:bg-accent/50 transition-colors"
                    >
                      <CardContent className="pt-6">
                        <div className="space-y-3">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h3 className="font-semibold text-lg mb-1">
                                {note.title || "Untitled Note"}
                              </h3>
                              <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                                {note.body || "No content"}
                              </p>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditNoteDialog(note)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteNoteMutation.mutate(note.id)}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </div>

                          <div className="flex flex-wrap gap-2">
                            {getSourceBadge(note.source)}
                            {isMFO && note.families?.name && (
                              <Badge variant="outline">{note.families.name}</Badge>
                            )}
                          </div>

                          {note.contacts?.full_name && (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <LinkIcon className="h-4 w-4" />
                              <span>Contact: {note.contacts.full_name}</span>
                            </div>
                          )}

                          <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t">
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatDate(note.created_at)}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Meeting Dialog */}
      <CreateMeetingDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSuccess={() => queryClient.invalidateQueries({ queryKey: ["meetings"] })}
      />

      {/* Create/Edit Note Dialog */}
      <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingNote ? "Edit Note" : "New Note"}</DialogTitle>
            <DialogDescription>
              {editingNote ? "Update your note details." : "Create a new note to capture important information."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="note-title">Title</Label>
              <Input
                id="note-title"
                value={noteForm.title}
                onChange={(e) => setNoteForm({ ...noteForm, title: e.target.value })}
                placeholder="Note title..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="note-body">Content</Label>
              <Textarea
                id="note-body"
                value={noteForm.body}
                onChange={(e) => setNoteForm({ ...noteForm, body: e.target.value })}
                placeholder="Write your note..."
                rows={5}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Source</Label>
                <Select
                  value={noteForm.source}
                  onValueChange={(value) => setNoteForm({ ...noteForm, source: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual</SelectItem>
                    <SelectItem value="meeting">Meeting</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Link to Contact</Label>
                <Select
                  value={noteForm.contact_id}
                  onValueChange={(value) => setNoteForm({ ...noteForm, contact_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select contact..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {contacts.map((contact) => (
                      <SelectItem key={contact.id} value={contact.id}>
                        {contact.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveNote}
              disabled={createNoteMutation.isPending || updateNoteMutation.isPending}
            >
              {(createNoteMutation.isPending || updateNoteMutation.isPending) && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
              {editingNote ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
